import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePricingPolicies, usePricingMutations } from '@/hooks/usePricing'
import type { LocationPricingAssignment } from '@/types/pricing'

const DAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
]

interface AssignPolicyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locationId: string
  existingAssignments: LocationPricingAssignment[]
}

export function AssignPolicyDialog({
  open,
  onOpenChange,
  locationId,
  existingAssignments,
}: AssignPolicyDialogProps) {
  const { data: allPolicies } = usePricingPolicies()
  const mutations = usePricingMutations()

  const [policyId, setPolicyId] = useState('')
  const [priority, setPriority] = useState(0)
  const [allDays, setAllDays] = useState(true)
  const [activeDays, setActiveDays] = useState<number[]>([])
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [effectiveTo, setEffectiveTo] = useState('')
  const [notes, setNotes] = useState('')

  // Filter out already-assigned policies
  const assignedIds = new Set(existingAssignments.map((a) => a.policyId))
  const availablePolicies =
    allPolicies?.filter((p) => p.status === 'active' && !assignedIds.has(p.id)) ?? []

  const toggleDay = (day: number) => {
    setActiveDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutations.createAssignment.mutate(
      {
        location_id: locationId,
        policy_id: policyId,
        priority,
        active_days: allDays ? null : activeDays.sort(),
        effective_from: effectiveFrom || null,
        effective_to: effectiveTo || null,
        notes: notes || null,
      },
      {
        onSuccess: () => {
          onOpenChange(false)
          resetForm()
        },
      }
    )
  }

  const resetForm = () => {
    setPolicyId('')
    setPriority(0)
    setAllDays(true)
    setActiveDays([])
    setEffectiveFrom('')
    setEffectiveTo('')
    setNotes('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Assign Policy</DialogTitle>
            <DialogDescription>
              Assign a pricing policy to this location. Higher priority wins when multiple
              policies match.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Policy</Label>
              <Select value={policyId} onValueChange={setPolicyId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a policy..." />
                </SelectTrigger>
                <SelectContent>
                  {availablePolicies.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availablePolicies.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No available policies. Create one in Organization Settings first.
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label>Priority</Label>
              <Input
                type="number"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                min={0}
                max={100}
              />
              <p className="text-xs text-muted-foreground">
                0 = base rate, 10+ = overrides. Higher number wins.
              </p>
            </div>

            <div className="grid gap-2">
              <Label>Active Days</Label>
              <div className="flex items-center gap-2 mb-2">
                <Checkbox
                  id="all-days"
                  checked={allDays}
                  onCheckedChange={(v) => setAllDays(!!v)}
                />
                <label htmlFor="all-days" className="text-sm">
                  All Days
                </label>
              </div>
              {!allDays && (
                <div className="flex gap-2 flex-wrap">
                  {DAY_OPTIONS.map((d) => (
                    <label
                      key={d.value}
                      className="flex items-center gap-1.5 text-sm cursor-pointer"
                    >
                      <Checkbox
                        checked={activeDays.includes(d.value)}
                        onCheckedChange={() => toggleDay(d.value)}
                      />
                      {d.label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>From (optional)</Label>
                <Input
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label>To (optional)</Label>
                <Input
                  type="date"
                  value={effectiveTo}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Reason for assignment..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!policyId || mutations.createAssignment.isPending}
            >
              {mutations.createAssignment.isPending ? 'Assigning...' : 'Assign Policy'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
